// src/pages/VehicleV2/MaintenanceList.jsx
// Design aligned with ProductionBOMListing
// Backend: https://nissi-india.com/api/maintenanceList

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSave,
  FaUndo,
  FaArrowLeft,
  FaTrash,
  FaSyncAlt,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/maintenanceList`;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const MaintenanceList = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS), [branchTypeLS]);
  const userId = useMemo(() => toStr(userIdLS), [userIdLS]);
  const loginUserId = useMemo(() => {
    const sid =
      typeof window !== "undefined" ? sessionStorage.getItem("loginUserId") : "";
    return toStr(sid || userId);
  }, [userId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const vehicleId = useMemo(
    () => toInt(params.get("vehicleId")) || toInt(getLS("vid")) || 0,
    [params]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [branchName, setBranchName] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState(null);

  const [subassemblies, setSubassemblies] = useState([]);
  const [descriptions, setDescriptions] = useState([]);

  const [form, setForm] = useState({
    currentDate: todayDDMMYYYY(),
    subassemblyId: "",
    vehicleMainDetId: "",
    equipmentBasis: "",
    partNumber: "",
    quantity: "",
    specification: "",
    usageLimit: "",
    meterReading: "",
  });

  const [grid, setGrid] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState("");

  const [editing, setEditing] = useState({
    maintenanceId: null,
    changedDate: "",
    hrm: "",
    needsManualReading: false,
  });

  const isBusy =
    pageLoading || dropdownLoading || gridLoading || submitting || actionLoading;

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const disabledFieldClass = `w-full px-4 py-2 border rounded-lg ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-gray-400"
      : "bg-gray-100 border-gray-300 text-gray-600"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const resetDescriptionFields = useCallback(() => {
    setForm((p) => ({
      ...p,
      vehicleMainDetId: "",
      equipmentBasis: "",
      partNumber: "",
      quantity: "",
      specification: "",
      usageLimit: "",
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm((p) => ({
      ...p,
      subassemblyId: "",
      vehicleMainDetId: "",
      equipmentBasis: "",
      partNumber: "",
      quantity: "",
      specification: "",
      usageLimit: "",
      meterReading: "",
    }));
    setDescriptions([]);
    toast.info("Form reset");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditing({
      maintenanceId: null,
      changedDate: "",
      hrm: "",
      needsManualReading: false,
    });
  }, []);

  const loadSubassemblies = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/subassemblies`, {
      params: { vehicleId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load subassemblies");
    setSubassemblies(data.list || []);
  }, [vehicleId, commonHeaders]);

  const loadDescriptions = useCallback(
    async (subassemblyId) => {
      if (!subassemblyId) {
        setDescriptions([]);
        return;
      }
      const { data } = await axios.get(`${API_BASE}/descriptions`, {
        params: { vehicleId, subassemblyId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load descriptions");
      setDescriptions(data.list || []);
    },
    [vehicleId, commonHeaders]
  );

  const loadDescriptionDetails = useCallback(
    async (vehicleMainDetId) => {
      if (!vehicleMainDetId) return;
      const { data } = await axios.get(`${API_BASE}/description-details`, {
        params: { vehicleMainDetId, vehicleId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load description details");
      const d = data.details || null;
      setForm((p) => ({
        ...p,
        equipmentBasis: d?.equipmentBasis || "",
        partNumber: d?.partNumber || "",
        quantity: d?.quantity || "",
        specification: d?.specification || "",
        usageLimit: d?.usageLimit || "",
      }));
    },
    [vehicleId, commonHeaders]
  );

  const loadHrKm = useCallback(
    async (dateStr) => {
      if (!isDDMMYYYY(dateStr)) return;
      const { data } = await axios.get(`${API_BASE}/hrkm`, {
        params: { vehicleId, date: dateStr },
        headers: commonHeaders,
      });
      if (!data?.success) {
        toast.warn(data?.message || "No progress entry for selected date");
        setForm((p) => ({ ...p, meterReading: "" }));
        return;
      }
      setForm((p) => ({
        ...p,
        equipmentBasis: data.basis || p.equipmentBasis,
        meterReading: data.reading ?? "",
      }));
    },
    [vehicleId, commonHeaders]
  );

  const loadGrid = useCallback(
    async (dateStr) => {
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { vehicleId, currentDate: dateStr },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load list");
      setVehicleBasis(data.vehicleBasis || "");
      setGrid(data.list || []);
    },
    [vehicleId, commonHeaders]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(vehicleId, "vehicleId missing. Pass ?vehicleId=123 or set localStorage 'vid'"))
      return;
    if (!canUse(toInt(branchId), "branchId missing. Set localStorage 'branchId'/'branchid'"))
      return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { vehicleId, branchId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setBranchName(data.branchName || "");
      setVehicleInfo(data.vehicleInfo || null);

      const curDate = form.currentDate || todayDDMMYYYY();
      await Promise.all([loadSubassemblies(), loadHrKm(curDate), loadGrid(curDate)]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init error");
    } finally {
      setPageLoading(false);
    }
  }, [
    vehicleId,
    branchId,
    commonHeaders,
    canUse,
    form.currentDate,
    loadSubassemblies,
    loadHrKm,
    loadGrid,
  ]);

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  }, []);

  const onCurrentDateChange = useCallback(
    async (e) => {
      const value = e.target.value;
      setForm((p) => ({ ...p, currentDate: value }));
      if (!isDDMMYYYY(value)) return;
      try {
        setGridLoading(true);
        await loadHrKm(value);
        await loadGrid(value);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Date load error");
      } finally {
        setGridLoading(false);
      }
    },
    [loadHrKm, loadGrid]
  );

  const onSubassemblyChange = useCallback(
    async (e) => {
      const subassemblyId = e.target.value;
      setForm((p) => ({
        ...p,
        subassemblyId,
        vehicleMainDetId: "",
        partNumber: "",
        quantity: "",
        specification: "",
        usageLimit: "",
      }));
      setDescriptions([]);
      if (!subassemblyId) return;
      try {
        setDropdownLoading(true);
        await loadDescriptions(subassemblyId);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Load descriptions error");
      } finally {
        setDropdownLoading(false);
      }
    },
    [loadDescriptions]
  );

  const onDescriptionChange = useCallback(
    async (e) => {
      const vehicleMainDetId = e.target.value;
      setForm((p) => ({ ...p, vehicleMainDetId }));
      if (!vehicleMainDetId) {
        resetDescriptionFields();
        return;
      }
      try {
        setDropdownLoading(true);
        await loadDescriptionDetails(vehicleMainDetId);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Load detail error");
      } finally {
        setDropdownLoading(false);
      }
    },
    [loadDescriptionDetails, resetDescriptionFields]
  );

  const submit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehicleId, "vehicleId missing")) return;
    if (!canUse(isDDMMYYYY(form.currentDate), "Current Date must be dd/MM/yyyy")) return;
    if (!canUse(form.subassemblyId, "Select Sub Assembly")) return;
    if (!canUse(form.vehicleMainDetId, "Select Description")) return;

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${API_BASE}/submit`,
        {
          vehicleId,
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          currentDate: form.currentDate,
          subassemblyId: toInt(form.subassemblyId),
          vehicleMainDetId: toInt(form.vehicleMainDetId),
          meterReading: form.meterReading,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Submit failed");
      toast.success(data.message || "Saved");
      await loadGrid(form.currentDate);
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Submit error");
    } finally {
      setSubmitting(false);
    }
  }, [loginUserId, branchId, vehicleId, form, commonHeaders, canUse, loadGrid, resetForm]);

  const deleteRow = useCallback(
    async (maintenanceId) => {
      if (!maintenanceId) return;
      if (!window.confirm("Delete this record?")) return;
      try {
        setActionLoading(true);
        const { data } = await axios.post(
          `${API_BASE}/delete`,
          { maintenanceId },
          { headers: commonHeaders }
        );
        if (!data?.success) throw new Error(data?.message || "Delete failed");
        toast.success("Deleted");
        await loadGrid(form.currentDate);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Delete error");
      } finally {
        setActionLoading(false);
      }
    },
    [commonHeaders, loadGrid, form.currentDate]
  );

  const startEdit = useCallback(
    (row) => {
      setEditing({
        maintenanceId:
          row.fld_maintenanceid ?? row.fld_maintenanceId ?? row.maintenanceId ?? null,
        changedDate: row.fld_curdate ? String(row.fld_curdate) : form.currentDate,
        hrm: row.fld_hrmtrreading ? String(row.fld_hrmtrreading) : "",
        needsManualReading: false,
      });
    },
    [form.currentDate]
  );

  const updateDate = useCallback(async () => {
    if (!editing.maintenanceId) return;
    if (!canUse(isDDMMYYYY(editing.changedDate), "Changed Date must be dd/MM/yyyy")) return;
    try {
      setActionLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/update-date`,
        {
          vehicleId,
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          maintenanceId: editing.maintenanceId,
          changedDate: editing.changedDate,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) {
        toast.error(data?.message || "Update date failed");
        return;
      }
      if (data.needsManualReading) {
        toast.info(data.message || "Enter Hour/KM manually");
        setEditing((p) => ({ ...p, needsManualReading: true }));
        return;
      }
      toast.success("Updated");
      await loadGrid(form.currentDate);
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Update date error");
    } finally {
      setActionLoading(false);
    }
  }, [
    editing,
    vehicleId,
    branchId,
    loginUserId,
    commonHeaders,
    canUse,
    loadGrid,
    form.currentDate,
    cancelEdit,
  ]);

  const updateHrm = useCallback(async () => {
    if (!editing.maintenanceId) return;
    if (!canUse(isDDMMYYYY(editing.changedDate), "Changed Date must be dd/MM/yyyy")) return;
    if (!canUse(editing.hrm && !Number.isNaN(Number(editing.hrm)), "Hour/KM reading must be numeric"))
      return;
    try {
      setActionLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/update-hrm`,
        {
          vehicleId,
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          maintenanceId: editing.maintenanceId,
          changedDate: editing.changedDate,
          hrm: editing.hrm,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Update HRM failed");
      toast.success("Updated");
      await loadGrid(form.currentDate);
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Update HRM error");
    } finally {
      setActionLoading(false);
    }
  }, [
    editing,
    vehicleId,
    branchId,
    loginUserId,
    commonHeaders,
    canUse,
    loadGrid,
    form.currentDate,
    cancelEdit,
  ]);

  const back = useCallback(() => {
    navigate("/vehicle/PeriodEntryListing");
  }, [navigate]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAINTENANCE LIST</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Maintenance List
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle: {vehicleInfo?.vehicleCode || "-"}
                {vehicleInfo?.vehicleRegNo ? ` (${vehicleInfo.vehicleRegNo})` : ""}
                {" • "}Branch: {branchName || "-"}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={back}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>
              <button
                onClick={loadInit}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt />
                Refresh
              </button>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                Entry Details
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={resetForm}
                  type="button"
                  disabled={isBusy}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <FaUndo /> Reset
                  </span>
                </button>
                <button
                  onClick={submit}
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSave />
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Equipment Code</label>
                <input value={vehicleInfo?.vehicleCode || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Branch Name</label>
                <input value={branchName || ""} disabled className={disabledFieldClass} />
              </div>

              <div>
                <label className={labelClass}>
                  Sub Assembly <span className="text-red-500">*</span>
                </label>
                <select
                  name="subassemblyId"
                  value={form.subassemblyId}
                  onChange={onSubassemblyChange}
                  className={fieldClass}
                  disabled={isBusy}
                >
                  <option value="">Select</option>
                  {subassemblies.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Description <span className="text-red-500">*</span>
                </label>
                <select
                  name="vehicleMainDetId"
                  value={form.vehicleMainDetId}
                  onChange={onDescriptionChange}
                  className={fieldClass}
                  disabled={!form.subassemblyId || isBusy}
                >
                  <option value="">Select</option>
                  {descriptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Equipment Basis</label>
                <input value={form.equipmentBasis || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Part Number / Oil Grade</label>
                <input value={form.partNumber || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Specification</label>
                <input value={form.specification || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Usage Limit</label>
                <input value={form.usageLimit || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Quantity</label>
                <input value={form.quantity || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Hour/Kilo Meter Reading</label>
                <input
                  name="meterReading"
                  value={form.meterReading}
                  onChange={onChange}
                  className={fieldClass}
                  placeholder="Auto-filled if available"
                  disabled={isBusy}
                />
              </div>
              <div>
                <label className={labelClass}>Current Date (dd/MM/yyyy)</label>
                <input
                  name="currentDate"
                  value={form.currentDate}
                  onChange={onCurrentDateChange}
                  className={fieldClass}
                  placeholder="dd/MM/yyyy"
                  disabled={isBusy}
                />
              </div>
              <div>
                <label className={labelClass}>Equipment Type</label>
                <input value={vehicleInfo?.vehicleName || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Equipment Number</label>
                <input value={vehicleInfo?.vehicleCode || ""} disabled className={disabledFieldClass} />
              </div>
              <div>
                <label className={labelClass}>Equipment Name</label>
                <input value={vehicleInfo?.vehicleRegNo || ""} disabled className={disabledFieldClass} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
              Periodic List
            </h3>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Basis: <b>{vehicleBasis || "-"}</b> • Records: <b>{grid.length}</b>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : grid.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Records found
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Maintenance ID</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Description</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Part No</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Limit</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Reading</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Current HR/KM</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Remaining</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {grid.map((r, idx) => {
                    const maintenanceId =
                      r.fld_maintenanceid ?? r.fld_maintenanceId ?? r.maintenanceId ?? "";
                    const desc = r.fld_Description ?? r.description ?? "";
                    const part = r.fld_Partnumber ?? r.partNumber ?? "";
                    const limit = r.fld_usagelimit ?? "";
                    const reading = r.fld_hrmtrreading ?? "";
                    const current = r.fld_hrskm ?? "";
                    const remaining = r.fld_Remaininghr ?? "";
                    const status = r.serviceStatus ?? "";
                    const isEditing = editing.maintenanceId === maintenanceId;

                    return (
                      <tr
                        key={`${maintenanceId}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {maintenanceId}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
                          {desc || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {part || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {limit || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {reading || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {current || "-"}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {remaining || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-semibold ${
                              status === "Service Expired"
                                ? "bg-red-600 text-white"
                                : status === "Service Nearly"
                                ? "bg-yellow-500 text-black"
                                : isDark
                                ? "bg-gray-700 text-gray-200"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {status || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {!isEditing ? (
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => startEdit(r)}
                                disabled={isBusy}
                                className={`px-3 py-1 rounded-md text-white text-xs font-semibold ${buttonClass} ${hoverClass} ${
                                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteRow(maintenanceId)}
                                disabled={isBusy}
                                className={`px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white ${
                                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2 min-w-[280px]">
                              <div className="grid grid-cols-1 gap-2">
                                <input
                                  value={editing.changedDate}
                                  onChange={(e) =>
                                    setEditing((p) => ({ ...p, changedDate: e.target.value }))
                                  }
                                  className={fieldClass}
                                  placeholder="dd/MM/yyyy"
                                  disabled={isBusy}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={updateDate}
                                    disabled={isBusy}
                                    className={`flex-1 px-3 py-2 rounded-md text-white text-xs font-semibold ${buttonClass} ${hoverClass} disabled:opacity-60`}
                                  >
                                    Update Date
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    disabled={isBusy}
                                    className={`px-3 py-2 rounded-md text-xs font-semibold ${
                                      isDark
                                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                                        : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                                    }`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                              {editing.needsManualReading && (
                                <div className="grid grid-cols-1 gap-2">
                                  <input
                                    value={editing.hrm}
                                    onChange={(e) =>
                                      setEditing((p) => ({ ...p, hrm: e.target.value }))
                                    }
                                    className={fieldClass}
                                    placeholder="Enter Hour/KM reading"
                                    disabled={isBusy}
                                  />
                                  <button
                                    onClick={updateHrm}
                                    disabled={isBusy}
                                    className="px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-semibold disabled:opacity-60"
                                  >
                                    Update Reading
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {isBusy && (
            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : dropdownLoading
                ? "Loading dropdown..."
                : gridLoading
                ? "Loading grid..."
                : submitting
                ? "Submitting..."
                : actionLoading
                ? "Processing..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default MaintenanceList;